import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";

import { boundedNote } from "../board.ts";
import {
  featureRow,
  listingNote,
  ML_FEATURES_SCOPE,
  MODEL_DEPLOYMENTS_SCOPE,
  REGISTER_REFUSAL,
  statesPresent,
  stateOf,
  type MlFeature,
  type ModelDeployment,
} from "../model-serving.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `ml` in `16 WP15 Platform Machinery.dc.html`: model serving and features.
 *
 * ```
 * ml:['Model serving and features','ML-01 · ML-02',
 *   'For every feature, freshness is the field that matters.',
 *   [['Models live','6','done','serving'],['Stale features','2','caution','fallback in use'],
 *    ['Missing features','1','failed','fallback'],['Shadow tests','1','active','']],
 *   ['Feature','Freshness','Fallback'],
 *   [['eta.traffic_speed','8 s','—','done','fresh'],
 *    ['demand.zone_forecast','41 min','last good value','caution','stale · serving fallback'],
 *    ['driver.acceptance_rate','missing','platform average','failed','missing · serving fallback']],
 *   'info','Four-way: fresh, stale with fallback, missing with fallback, unavailable.']
 * ```
 *
 * What is drawn from data and what is not:
 *
 * | drawn                                   | source                                                     |
 * | --------------------------------------- | ---------------------------------------------------------- |
 * | the rows                                | `ML-02 GET /v1/platform/ml-features` (ADR 0067)            |
 * | `Feature` — `eta.traffic_speed`         | `features.name`; `featureId` is the URN, in the panel      |
 * | `Freshness` — `8 s`, `41 min`           | `now - (newestExpiresAt - maximumStalenessSeconds)`, exact |
 * | `Freshness` — `missing`                 | `newestExpiresAt === null`, the design's own word          |
 * | `Fallback` — `last good value`          | **nothing** — four strategies, one literal modelled        |
 * | `fresh`, `stale · serving fallback`, `missing · serving fallback` | derived per ADR 0067 §2   |
 * | `Models live · 6`                       | `ML-01 GET /v1/platform/model-deployments` (ADR 0068), as a sentence |
 * | `Shadow tests · 1`                      | **nothing** — no traffic split, no shadow flag, unmodelled |
 * | `Stale features · 2`, `Missing features · 1` | computable, and no tile row is drawn here (Q-178)     |
 * | the primary command                     | drawn and refused — `REGISTER_REFUSAL`, ADR 0067 §10       |
 *
 * ## Two listings on one page, and only one of them is a row
 *
 * The source chip names two services and both answer something, which is true of one other page of
 * this console. **The design draws one grid, and all three of its columns are ML-02's** — ADR 0068
 * §7 says ML-01's listing "answers one tile and no column" and argues that rather than hiding it.
 * So the rows are the features, and ML-01 answers a count that this board says in a sentence under
 * the grid, because the designed tile row is not drawn on any board of this console.
 *
 * Unlike `EventCatalogue.tsx`, the secondary listing fills **no panel line either**: nothing
 * publishes a relation between a feature and a deployment, and the only field that could name one —
 * `inputFields` — is withheld as the model's feature vector and the inference allowlist. See
 * `../model-serving.ts`'s header; a row asserting which model consumes a feature would be a wrong
 * answer where a missing one is available.
 *
 * ## Both scopes are required and only one listing is load-bearing
 *
 * Both are asked for before either call, so a missing grant names both scopes in one actionable
 * sentence. After that the two listings are not equal and are not treated as equal:
 *
 *   - **ML-02 is the grid.** A failure there is the board's failure state; there is nothing to draw.
 *   - **ML-01 is one clause of one sentence.** A failure there degrades that clause and keeps every
 *     feature on screen. The alternative — one `Promise.all` and one error state — would hide every
 *     online feature on the platform because a second service was unreachable, which on a page
 *     whose subject is staleness is exactly the wrong failure.
 *
 * ## What a platform-scoped read did not give this page
 *
 * One column and one tile, and neither is route work in either service. The `Fallback` column's
 * four strategies are unmodelled — ML-02 holds one literal and `null`, and `last good value` is a
 * behaviour that service does the opposite of. `Shadow tests` needs a traffic split, a shadow flag
 * or a comparison target, and `shadow` and `canary` appear nowhere under
 * `services/machine-learning/`. Both are new aggregates rather than projections of existing ones.
 */

const featureServing = services.machine_learning__feature_serving;
const modelServing = services.machine_learning__model_serving_gateway;

export interface ModelServingProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Platform › Risk and data › Model serving and features`, the design's trail. */
  readonly crumbs: readonly string[];
  /**
   * The instant freshness is read against, where a caller has one.
   *
   * Optional, because the fidelity harness pins `Date` itself — `console-boards.mts` installs a
   * fixed `Date.now()` on both sides, so a capture taken a day later is the same capture. It is
   * accepted all the same so a test can hand this board an instant without replacing a global, and
   * because an age derived from a clock nobody can name is the one value on this page that would
   * differ between two renders for no reason the reader could see.
   */
  readonly now?: number;
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Model serving and features";
/**
 * The design's own source chip, word for word: `ML-01 · ML-02`.
 *
 * ML-01 first, although ML-02 is every row — the order is the design's, and `navigation.ts` lists
 * the same two owners in the same order.
 */
const SOURCE = "ML-01 · ML-02";
const DESCRIPTION = "For every feature, freshness is the field that matters.";
const COLUMNS = ["Feature", "Freshness", "Fallback"] as const;
const NOTE = "Four-way: fresh, stale with fallback, missing with fallback, unavailable.";
/** The design's own `noteK` for this page is `info`, which its `NK` map draws in the sky tint. */
const NOTE_KIND = "info" as const;
/** The design's own `FORMS.ml[0]`, which is the word on its heading button. */
const PRIMARY = "Register feature";

/**
 * The most rows one request answers, which is both services' own maximum.
 *
 * One request per listing, and ML-01's is asked for at the same bound rather than a smaller one:
 * a deployment page cut short would make the live count a smaller floor than it needs to be, and
 * `listingNote` says it is a floor either way.
 */
const PER_FETCH = 200;

/**
 * Narrow one published row onto the type the contract describes.
 *
 * The generated **client method** types `newestExpiresAt` as `unknown`, because the contract writes
 * its nullability as `type: [string, "null"]` and the generator does not narrow that form; the
 * generated `PlatformMlFeatureSummary` type beside it says `string | null`. So the narrowing happens
 * once, here at the edge, by `typeof` rather than by a cast — a cast would assert a guarantee this
 * console did not check, and anything that is not a string is read as the absence the contract's
 * other branch permits.
 */
function featureOf(row: {
  tenantId: string;
  featureId: string;
  name: string;
  version: number;
  maximumStalenessSeconds: number;
  hasFallback: boolean;
  newestExpiresAt: unknown;
}): MlFeature {
  return {
    tenantId: row.tenantId,
    featureId: row.featureId,
    name: row.name,
    version: row.version,
    maximumStalenessSeconds: row.maximumStalenessSeconds,
    hasFallback: row.hasFallback,
    newestExpiresAt: typeof row.newestExpiresAt === "string" ? row.newestExpiresAt : null,
  };
}

export function ModelServing({ transport, session, crumbs, now }: ModelServingProps): ReactNode {
  const [rows, setRows] = useState<readonly MlFeature[] | undefined>(undefined);
  const [deployments, setDeployments] = useState<readonly ModelDeployment[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [deploymentsTruncated, setDeploymentsTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");
  /*
   * One instant for the whole board, read when the listing is asked for rather than when a row is
   * drawn.
   *
   * Every `Freshness` cell and every badge on this page is a comparison against a clock, and reading
   * the clock per render would let two rows of one answer be aged against two different instants —
   * and would make a row's age change on a keystroke in the search box, which is a value moving for
   * a reason the reader cannot see. A retry re-reads it, which is right: the ages are the ages at
   * the moment the page was fetched.
   */
  const [readAt, setReadAt] = useState(() => now ?? Date.now());

  const load = useCallback(async () => {
    setRows(undefined);
    setDeployments(undefined);
    setFailure(undefined);
    setReadAt(now ?? Date.now());

    /*
     * Both scopes asked for before either call, not discovered from a refusal.
     *
     * One state naming both, because that is what an operator can act on: they can ask their
     * administrator for `feature-serving:platform:read` and `model-serving:platform:read` by name. A
     * 403 from either service renders the same state, so this is not the only guard; it is the one
     * that makes both scopes literals in this application's source, which is what
     * `channel-scopes.mjs` derives the Keycloak grant from. ADR 0049 §3.
     */
    const missing = [ML_FEATURES_SCOPE, MODEL_DEPLOYMENTS_SCOPE].filter(
      (scope) => !session.scopes.has(scope),
    );
    if (missing.length > 0) {
      setFailure({ kind: "forbidden", missingScopes: missing });
      return;
    }

    /*
     * `listPlatformMlFeatures`, and the path is `/v1/platform/ml-features` rather than
     * `/v1/platform/features` — CFG-02 holds the latter for **feature flags**, which this console
     * already draws on its `pflags` board. ADR 0067 §1 settles the collision on the path; calling
     * the wrong one here would answer a page of rollout configuration under a grid headed
     * `Freshness`.
     *
     * ML-02's three others are all `/internal/v1`, and the entity read is the one no board can call
     * at all: it needs an `entityKey`, which is a driver, a rider or a zone.
     *
     * Started together and awaited apart, which is the asymmetry this file's header argues: the
     * features are the grid and the deployments are one clause of one sentence.
     */
    const featurePage = featureServing
      .createFeatureServingClient(transport)
      .listPlatformMlFeatures(String(PER_FETCH));
    const deploymentPage = modelServing
      .createModelServingGatewayClient(transport)
      .listPlatformModelDeployments(String(PER_FETCH));

    /*
     * The deployment listing's rejection is caught here and not left for the `await` below, because
     * an unhandled rejection on a promise started before an earlier `await` threw is an unhandled
     * rejection warning rather than a handled failure.
     */
    const live = deploymentPage.then(
      (page) => page,
      () => undefined,
    );

    try {
      const page = await featurePage;
      setRows(page.features.map(featureOf));
      setTruncated(typeof page.nextAfter === "string");
    } catch (error) {
      setFailure(stateForError(error, [ML_FEATURES_SCOPE]));
      return;
    }

    const served = await live;
    setDeployments(served?.deployments);
    setDeploymentsTruncated(served !== undefined && typeof served.nextAfter === "string");
  }, [transport, session.scopes, now]);

  useEffect(() => {
    void load();
  }, [load]);

  const at = readAt;
  const boardRows =
    rows === undefined
      ? undefined
      : rows
          .filter((feature) => state === "" || stateOf(feature, at).label === state)
          .map((feature) => featureRow(feature, at));

  const bounded = boundedNote(
    rows?.length,
    truncated,
    "online features",
    "ordered by tenant, then feature",
  );
  const footnote = listingNote(rows, deployments, deploymentsTruncated);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No feature is registered on any tenant. A row appears here when an operator registers an online feature with ML-02."
      primary={{ label: PRIMARY, refusal: REGISTER_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows, at)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "info", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(footnote === undefined ? {} : { footnote })}
      exportName="model-serving-features"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
